import React, { useState, useEffect } from "react";
import { toast, Toaster, ToastBar } from "react-hot-toast";
import ListDetailsSubLayout from "../../layout/ListDetailSubLayout";
import ResidualReports from "./components/reports";
import SearchInput from "../../components/SearchInput/SearchInput";
import ButtonIcon from "../../components/ButtonIcon/ButtonIcon";
import Filter from "../../assets/svg/icons/common/Filter";
import MonthPicker from "../../components/form/month-picker";
import DropDown from "../../components/DropDown/DropDown";
import {
  parse,
  startOfMonth,
  endOfMonth,
  format,
  startOfYear,
  endOfYear,
  subYears,
  subMonths,
} from "date-fns";
import useCommonStore from "../../stores/common";
import useResidualStore from "../../stores/residuals";
import ResidualStats from "./components/ResidualStats";
import ResidualTable from "./components/ResidualTable";
import ResidualCharts from "./components/ResidualCharts";
import FilterDrawer from "./components/FilterDrawer";
import ReportMobileHeaderActions from "./components/reports/mobile-header-actions";
import MobileDesktopAlert from "../../components/MobileDesktopAlert";
import RedDotBadge from "../../assets/svg/icons/common/RedDotBadge";
import CloseIconWhite from "../../assets/svg/icons/common/CloseIconWhite";
import styles from "./style.module.scss";

const getResidualFilterCount = (filters) => {
  if (!filters) return 0;

  const hasOrgFilter = filters.orgFilter !== null && filters.orgFilter !== "";
  const cardsCount = filters.cards?.length ?? 0;
  const officesCount = filters.offices?.length ?? 0;
  const branchesCount = filters.branches?.length ?? 0;
  const repCodesCount = filters.repCodes?.length ?? 0;

  return [
    hasOrgFilter,
    cardsCount,
    officesCount,
    branchesCount,
    repCodesCount,
  ].reduce((sum, val) => {
    if (typeof val === "number") {
      return sum + val;
    }
    return sum + (val ? 1 : 0);
  }, 0);
};

const ResidualsPage = () => {
  const {
    filters,
    setFilters,
    fetchResidualsData,
    loading,
    filterApplied,
    setFilterApplied,
    setSelectedReport,
  } = useResidualStore();
  const setShowDetails = useCommonStore((state) => state.setShowDetails);
  const setExpandDetails = useCommonStore((state) => state.setExpandDetails);
  const filterCount = getResidualFilterCount(filters);
  const setFilterDrawerOpen = useCommonStore(
    (state) => state.setFilterDrawerOpen,
  );

  // Keep track of the current year
  const [currentYear, setCurrentYear] = useState(() => {
    const date = parse(filters.dateRange.startDate, "MM/dd/yyyy", new Date());
    return date.getFullYear();
  });

  // Function to get yearly date range
  const getYearlyDateRange = (year) => ({
    startDate: format(new Date(year, 0, 1), "MM/dd/yyyy"), // January 1st
    endDate: format(new Date(year, 11, 31), "MM/dd/yyyy"), // December 31st
    rangeType: "YTD",
  });

  // Single effect to handle all data fetching
  const fetchData = async (filtersToUse) => {
    try {
      // Get the year from the date range
      const newDate = parse(
        filtersToUse.dateRange.startDate,
        "MM/dd/yyyy",
        new Date(),
      );
      const newYear = newDate.getFullYear();

      // Update current year if changed
      if (newYear !== currentYear) {
        setCurrentYear(newYear);
      }

      // Fetch data with the provided filters
      await fetchResidualsData(filtersToUse);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch residuals data", { duration: 5000 });
    }
  };

  // Reset all filters to their default values on component mount
  useEffect(() => {
    const defaultFilters = {
      searchTerm: "",
      dateRange: {
        startDate: format(startOfMonth(subMonths(new Date(), 1)), "MM/dd/yyyy"),
        endDate: format(endOfMonth(subMonths(new Date(), 1)), "MM/dd/yyyy"),
        rangeType: "MTD",
      },
      cards: [],
      offices: [],
      branches: [],
      repCodes: [],
      orgFilter: "",
      selectedFilters: [],
      sort: {
        field: "dba",
        direction: "asc",
      },
      pagination: {
        page: 1,
        per_page: 10,
      },
      displayMode: "hierarchy",
    };
    setFilters(defaultFilters);
    setFilterApplied(true);
  }, []);

  // Fetch data when filters are applied
  useEffect(() => {
    if (filterApplied) {
      fetchData(filters);
    }
  }, [filterApplied, filters]);

  const handleSearchChange = (e) => {
    const newFilters = {
      ...filters,
      searchTerm: e.target.value,
    };
    setFilters(newFilters);
    setFilterApplied(true);
  };

  const handleMonthPickerChange = (data) => {
    let startDate, endDate;
    const currentDate = new Date();
    let rangeType = "MTD";

    if (["MTD", "LTY", "YTD"].includes(data.month)) {
      rangeType = data.month;
      if (data.month === "YTD") {
        startDate = startOfYear(currentDate);
        endDate = currentDate;
      } else if (data.month === "MTD") {
        startDate = startOfMonth(currentDate);
        endDate = currentDate;
      } else if (data.month === "LTY") {
        const lastYearDate = subYears(currentDate, 1);
        startDate = startOfYear(lastYearDate);
        endDate = endOfYear(lastYearDate);
      }
    } else {
      rangeType = "MTD";
      const parsedDate = parse(
        `${data.month} ${data.year}`,
        "MMM yyyy",
        new Date(),
      );
      startDate = parsedDate;
      endDate = endOfMonth(parsedDate);
    }

    const newFilters = {
      ...filters,
      dateRange: {
        startDate: format(startDate, "MM/dd/yyyy"),
        endDate: format(endDate, "MM/dd/yyyy"),
        rangeType: rangeType,
      },
    };
    setFilters(newFilters);
    setFilterApplied(true);
  };

  const handleReportOpen = (report) => {
    setSelectedReport(report);
    setShowDetails(true);
    setExpandDetails(true);
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={`${styles.page_heading} ${styles.column}`}>
          <div className={styles.title_contents}>
            <p>Residuals</p>
          </div>
          <div className={styles.search_input_container}>
            <div className={styles.search_wrapper}>
              <SearchInput
                onChange={handleSearchChange}
                searchValue={filters.searchTerm}
              />
            </div>
            <div className={styles.month_picker_wrapper}>
              <MonthPicker
                variant="smallFilter"
                onSubmit={handleMonthPickerChange}
                changeOnSubmit
                showYearFilters
                showActions
                supportMobile
                defaultDate={subMonths(new Date(), 1)}
              />
            </div>
            <DropDown
              bgColor="#ededf4"
              showArrow
              options={[
                {
                  text: "Payment Authorization",
                  onClick: () => handleReportOpen("payment-authorization"),
                },
                {
                  text: "Invoice Statement",
                  onClick: () => handleReportOpen("invoice-statement"),
                },
              ]}
              className={styles.report_dropdown}
            >
              Report
            </DropDown>
            <ButtonIcon
              bgColor="#EDEDF4"
              onClick={() => setFilterDrawerOpen(true)}
              className={styles.filter_button}
            >
              <span className={styles.filterIconWrapper}>
                <Filter />
                {filterCount > 0 && (
                  <RedDotBadge className={styles.filter_active} />
                )}
              </span>
              <span className={styles.filter_button_label}>
                Filter
                {filterCount > 0 ? `: ${filterCount}` : ""}
              </span>
            </ButtonIcon>
          </div>
        </div>

        <ResidualStats />
        <ResidualCharts />

        <div className={styles.desktop_table}>
          <ResidualTable />
        </div>

        <div className={styles.mobile_alert}>
          <MobileDesktopAlert />
        </div>
      </div>
      <FilterDrawer />

      {/* Toast notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: "#FFFFFF",
            color: "#3A383F",
            boxShadow:
              "0px 12px 28px 0px rgba(30, 12, 61, 0.07), 0px 4px 4px 0px rgba(30, 12, 61, 0.03)",
            borderRadius: "8px",
            fontSize: "14px",
          },
        }}
      >
        {(t) => (
          <ToastBar toast={t}>
            {({ icon, message }) => (
              <>
                {icon}
                {message}
                <button
                  type="button"
                  className={styles.toast_dismissal_button}
                  onClick={() => toast.dismiss(t.id)}
                >
                  <CloseIconWhite />
                </button>
              </>
            )}
          </ToastBar>
        )}
      </Toaster>
    </div>
  );
};

const Residuals = () => {
  const commonStore = useCommonStore();
  const residualReport = useResidualStore((state) => state.selectedReport);
  const residualReportSelected =
    residualReport !== null && residualReport !== "";
  useEffect(() => {
    return () => {
      commonStore.reset();
    };
  }, []);

  return (
    <ListDetailsSubLayout
      detailsComponent={ResidualReports}
      listComponent={ResidualsPage}
      mobileHeaderActions={
        residualReportSelected ? <ReportMobileHeaderActions /> : null
      }
    />
  );
};

export default Residuals;
